import { Flex, Heading, Pill, View } from "@instructure/ui";
import { createContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Skeleton from "react-loading-skeleton";
import { Outlet, useLocation } from "react-router";
import {
  ERROR,
  useNotification,
} from "../../components/CustomHooks/NotificationProvider";
import { ActionService } from "../../services/ActionService";
import { ReflectionService } from "../../services/ReflectionService";
import { ReflectionTemplateService } from "../../services/ReflectionTemplateService";
import { ReviewService } from "../../services/ReviewService";
import { EMPTY_RTE } from "../CustomTextArea/CustomRTE";
import CourseReviewFooter from "./CourseReviewFooter";
import { INITIAL_COURSE_REVIEW } from "./utils";

export const CourseReviewContext = createContext();

export const CourseReview = () => {
  const { t } = useTranslation();
  const showNotification = useNotification();
  const location = useLocation();
  const offering = location.state.section.offering;
  const review = location.state.review;

  const [loading, setLoading] = useState(true);
  const [reviews, setReviews] = useState([]);
  const [reflections, setReflections] = useState([]);
  const [actions, setActions] = useState([]);
  const [actionTypes, setActionTypes] = useState([]);

  const [draftData, setDraftData] = useState(fetchDraftData());

  function fetchDraftData() {
    if (review) {
      const storedDraft = sessionStorage.getItem("courseReview/" + review.id);
      if (storedDraft) {
        return JSON.parse(storedDraft);
      }
    }
    return INITIAL_COURSE_REVIEW;
  }

  useEffect(() => {
    async function fetchReflectionsAndActions() {
      try {
        setLoading(true);
        const [reviews, reflections, actions, actionTypes, templates] =
          await Promise.all([
            ReviewService.getReviewsByContextId(offering.parent_course_id),
            ReflectionService.getCourseReflectionsByOfferingId(offering.id),
            ActionService.getActionsForCourseByOfferingId(offering.id),
            ActionService.getActionTypes(),
            ReflectionTemplateService.getTemplates(),
          ]);

        setReviews(reviews);
        setActions(actions);
        setActionTypes(
          actionTypes.sort((a, b) => a.name.localeCompare(b.name))
        );

        const filteredReflections = [];

        for (const reflection of reflections) {
          if (templates) {
            if (reflection.from_template) {
              if (!filteredReflections.some((r) => r.id === reflection.id)) {
                const template = templates.find(
                  (t) => t.id === reflection.template_id
                );
                const groupedReflections = reflections.filter(
                  (r) => r.template_id === template.id
                );

                const templateReflection = {
                  ...reflection,
                  from_template: true,
                  sections: template.sections.map((section) => ({
                    id: section.id,
                    content: groupedReflections.find(
                      (r) => r.section_id === section.id
                    ).content,
                    header_question: section.header_question,
                    helper_text: EMPTY_RTE,
                  })),
                };
                filteredReflections.push(templateReflection);
              }
            } else {
              filteredReflections.push(reflection);
            }
          } else {
            filteredReflections.push(reflection);
          }
        }

        filteredReflections.sort(
          (a, b) => new Date(b.timestamp) - new Date(a.timestamp)
        );
        setReflections(filteredReflections);
      } catch {
        showNotification(
          t("notifications.general.fetchError", {
            obj: t("reflections.reflections"),
          }),
          ERROR
        );
      } finally {
        setLoading(false);
      }
    }
    fetchReflectionsAndActions();
  }, [offering.parent_course_id, offering.id, showNotification]);

  return (
    <CourseReviewContext.Provider
      value={{
        reviews,
        reflections,
        actions,
        actionTypes,
        draftData,
        setDraftData,
      }}
    >
      <div
        style={{
          height: "calc(-3rem + 100vh)",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Flex justifyItems="space-around" wrap="wrap" gap="small">
          <Flex.Item shouldGrow>
            <Heading as="h1" level="h2">
              {offering.parent_course_code} {t("courseReview.courseReview")}
            </Heading>
          </Flex.Item>
          {review && (
            <Flex.Item>
              <Pill color="info">{review.academic_years[0].name}</Pill>
              {" - "}
              <Pill color="info">
                {review.academic_years[review.academic_years.length - 1].name}
              </Pill>
            </Flex.Item>
          )}
        </Flex>
        <View
          as="div"
          borderWidth="none none small none"
          margin="0 0 medium 0"
        />
        <div
          style={{
            flexGrow: 1,
            overflowY: "hidden",
            padding: "0.75rem",
            margin: "-0.75rem",
          }}
        >
          {loading ? (
            <>
              <Skeleton height="150px" />
              <Skeleton height="150px" style={{ margin: "1.5rem 0" }} />
              <Skeleton height="150px" />
            </>
          ) : (
            <Outlet />
          )}
        </div>

        <CourseReviewFooter setReviews={setReviews} />
      </div>
    </CourseReviewContext.Provider>
  );
};
